import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './Card';
import { Alert, AlertDescription } from './Alert';

// Componentes base seguindo o guia de estilo Unimed

/**
 * Card temático Unimed
 * Aplica automaticamente as cores e estilos da marca
 */
export const UnimedCard = ({ children, variant = 'default', className = '', ...props }) => {
  const variants = {
    default: 'unimed-card unimed-section',
    primary: 'unimed-card unimed-section bg-gradient-to-br from-unimed-green-50 to-white border-l-4 border-l-unimed-green-500',
    secondary: 'unimed-card unimed-section bg-gradient-to-br from-unimed-orange-50 to-white border-l-4 border-l-unimed-orange-500',
    accent: 'unimed-card unimed-section bg-gradient-to-br from-unimed-lightblue-50 to-white border-l-4 border-l-unimed-lightblue-500',
    neutral: 'unimed-card unimed-section bg-gradient-to-br from-unimed-beige-50 to-white border-l-4 border-l-unimed-beige-300'
  };

  const cardClass = variants[variant] || variants.default; // Fallback para default

  return (
    <Card className={`${cardClass} hover:shadow-lg transition-all duration-300 ${className}`} {...props}>
      {children}
    </Card>
  );
};

/**
 * Header de Card temático Unimed
 */
export const UnimedCardHeader = ({ children, className = '', ...props }) => {
  return (
    <CardHeader className={`border-b border-unimed-beige-200 ${className}`} {...props}>
      {children}
    </CardHeader>
  );
};

/**
 * Título de Card temático Unimed
 */
export const UnimedCardTitle = ({ children, size = 'xl', className = '', ...props }) => {
  const sizes = {
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl'
  };

  // Converte o texto para uppercase apenas se for string
  const formatChildren = (children) => {
    if (typeof children === 'string') {
      return children.toUpperCase();
    }
    return children;
  };

  return (
    <CardTitle className={`unimed-titulo-principal ${sizes[size]} font-semibold text-unimed-darkgreen-800 ${className}`} {...props}>
      {formatChildren(children)}
    </CardTitle>
  );
};

/**
 * Conteúdo de Card temático Unimed
 */
export const UnimedCardContent = ({ children, className = '', ...props }) => {
  return (
    <CardContent className={`${className}`} {...props}>
      {children}
    </CardContent>
  );
};

/**
 * Componente de métrica/KPI seguindo o tema Unimed
 */
export const UnimedMetricCard = ({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  variant = 'primary',
  loading = false,
  className = '',
  ...props 
}) => {
  const variants = {
    primary: {
      card: 'from-unimed-green-50 to-white border-l-unimed-green-500',
      title: 'text-unimed-green-700',
      value: 'text-unimed-green-800',
      subtitle: 'text-unimed-green-600',
      icon: 'from-unimed-green-500 to-unimed-green-600'
    },
    secondary: {
      card: 'from-unimed-orange-50 to-white border-l-unimed-orange-500',
      title: 'text-unimed-orange-700',
      value: 'text-unimed-orange-800',
      subtitle: 'text-unimed-orange-600',
      icon: 'from-unimed-orange-500 to-unimed-orange-600'
    },
    accent: {
      card: 'from-unimed-lightblue-50 to-white border-l-unimed-lightblue-500',
      title: 'text-unimed-lightblue-700',
      value: 'text-unimed-lightblue-800',
      subtitle: 'text-unimed-lightblue-600',
      icon: 'from-unimed-lightblue-500 to-unimed-lightblue-600'
    },
    neutral: {
      card: 'from-unimed-beige-50 to-white border-l-unimed-beige-500',
      title: 'text-unimed-darkgreen-700',
      value: 'text-unimed-darkgreen-800',
      subtitle: 'text-unimed-darkgreen-600',
      icon: 'from-unimed-darkgreen-500 to-unimed-darkgreen-600'
    }
  };

  const theme = variants[variant] || variants.primary; // Fallback para primary se variant for inválido

  if (loading) {
    return (
      <Card className={`bg-gradient-to-br ${theme.card} border-l-4 hover:shadow-lg transition-all duration-300 animate-pulse ${className}`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="h-4 bg-unimed-beige-200 rounded mb-2"></div>
              <div className="h-8 bg-unimed-beige-200 rounded mb-2"></div>
              <div className="h-3 bg-unimed-beige-200 rounded w-2/3"></div>
            </div>
            <div className="w-16 h-16 bg-unimed-beige-200 rounded-xl"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`bg-gradient-to-br ${theme.card} border-l-4 hover:shadow-lg transition-all duration-300 ${className}`} {...props}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className={`unimed-chapeu text-sm font-semibold ${theme.title} uppercase tracking-wider mb-1`}>
              {typeof title === 'string' ? title.toUpperCase() : title}
            </p>
            <p className={`unimed-titulo-principal text-3xl font-bold ${theme.value} mb-1`}>
              {value}
            </p>
            {subtitle && (
              <div className={`unimed-texto-corrido text-xs ${theme.subtitle}`}>
                {subtitle}
              </div>
            )}
          </div>
          {Icon && (
            <div className={`bg-gradient-to-br ${theme.icon} p-4 rounded-xl shadow-lg`}>
              <Icon className="h-8 w-8 text-white" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Tabela temática Unimed
 */
export const UnimedTable = ({ children, className = '', ...props }) => {
  return (
    <div className="overflow-x-auto">
      <table className={`min-w-full divide-y divide-unimed-beige-200 ${className}`} {...props}>
        {children}
      </table>
    </div>
  );
};

export const UnimedTableHeader = ({ children, className = '', ...props }) => {
  return (
    <thead className={`bg-unimed-green-50 ${className}`} {...props}>
      {children}
    </thead>
  );
};

export const UnimedTableHeaderCell = ({ children, className = '', ...props }) => {
  // Converte o texto para uppercase apenas se for string
  const formatChildren = (children) => {
    if (typeof children === 'string') {
      return children.toUpperCase();
    }
    return children;
  };

  return (
    <th className={`px-6 py-3 text-left unimed-chapeu text-xs font-semibold text-unimed-darkgreen-700 uppercase tracking-wider ${className}`} {...props}>
      {formatChildren(children)}
    </th>
  );
};

export const UnimedTableBody = ({ children, className = '', ...props }) => {
  return (
    <tbody className={`bg-white divide-y divide-unimed-beige-200 ${className}`} {...props}>
      {children}
    </tbody>
  );
};

export const UnimedTableRow = ({ children, index = 0, className = '', ...props }) => {
  return (
    <tr className={`${index % 2 === 0 ? 'bg-white' : 'bg-unimed-beige-50'} ${className}`} {...props}>
      {children}
    </tr>
  );
};

export const UnimedTableCell = ({ children, variant = 'default', className = '', ...props }) => {
  const variants = {
    default: 'unimed-texto-corrido text-sm text-unimed-darkgreen-700',
    bold: 'unimed-texto-corrido text-sm font-medium text-unimed-darkgreen-800',
    muted: 'unimed-texto-corrido text-sm text-unimed-darkgreen-600'
  };

  return (
    <td className={`px-6 py-4 whitespace-nowrap ${variants[variant]} ${className}`} {...props}>
      {children}
    </td>
  );
};

/**
 * Alerta temático Unimed
 */
export const UnimedAlert = ({ children, variant = 'info', className = '', ...props }) => {
  const variants = {
    success: 'success',
    warning: 'warning',
    error: 'destructive',
    info: 'default'
  };

  return (
    <Alert variant={variants[variant]} className={`${className}`} {...props}>
      {children}
    </Alert>
  );
};

export const UnimedAlertDescription = ({ children, className = '', ...props }) => {
  return (
    <AlertDescription className={`unimed-texto-corrido ${className}`} {...props}>
      {children}
    </AlertDescription>
  );
};

/**
 * Botão CTA Unimed
 */
export const UnimedButton = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  loading = false,
  className = '', 
  ...props 
}) => {
  const variants = {
    primary: 'unimed-cta',
    secondary: 'unimed-button-secondary',
    outline: 'unimed-button-outline'
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  };

  return (
    <button 
      className={`${variants[variant]} ${sizes[size]} ${loading ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
      disabled={loading}
      {...props}
    >
      {loading ? (
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          Carregando...
        </div>
      ) : (
        children
      )}
    </button>
  );
};

/**
 * Grid responsivo Unimed com espaçamento adequado
 */
export const UnimedGrid = ({ 
  children, 
  cols = 'auto', 
  gap = '6',
  className = '',
  ...props 
}) => {
  const colsClasses = {
    1: 'grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-2 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
    auto: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
  };

  return (
    <div className={`grid ${colsClasses[cols]} gap-${gap} ${className}`} {...props}>
      {children}
    </div>
  );
};

/**
 * Container principal com espaçamento Unimed
 */
export const UnimedContainer = ({ children, className = '', ...props }) => {
  return (
    <div className={`space-y-8 animate-fade-in ${className}`} {...props}>
      {children}
    </div>
  );
};

/**
 * Seção com título e conteúdo seguindo o padrão Unimed
 */
export const UnimedSection = ({ title, children, className = '', ...props }) => {
  // Converte o título para uppercase apenas se for string
  const formatTitle = (title) => {
    if (typeof title === 'string') {
      return title.toUpperCase();
    }
    return title;
  };

  return (
    <div className={`unimed-section ${className}`} {...props}>
      {title && (
        <h2 className="unimed-titulo-principal text-2xl text-unimed-darkgreen-800 mb-6">
          {formatTitle(title)}
        </h2>
      )}
      {children}
    </div>
  );
};

/**
 * Badge/Tag temático Unimed
 */
export const UnimedBadge = ({ children, variant = 'default', className = '', ...props }) => {
  const variants = {
    default: 'bg-unimed-green-100 text-unimed-green-800',
    success: 'bg-unimed-green-100 text-unimed-green-800',
    warning: 'bg-unimed-orange-100 text-unimed-orange-800',
    error: 'bg-red-100 text-red-800',
    info: 'bg-unimed-lightblue-100 text-unimed-lightblue-800',
    neutral: 'bg-unimed-beige-100 text-unimed-darkgreen-800'
  };

  const badgeClass = variants[variant] || variants.default; // Fallback para default

  return (
    <span className={`inline-flex px-2 py-1 unimed-texto-corrido text-xs font-semibold rounded-full ${badgeClass} ${className}`} {...props}>
      {children}
    </span>
  );
};
